/**
 * ============================================================================
 * BOOKINGS MANAGEMENT - PRODUCTION-READY COMPONENT
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE DEVELOPMENT:
 *
 * 1. TYPE SAFETY IS NON-NEGOTIABLE
 *    - NEVER use 'any' types
 *    - ALL types are defined in /types/bookings.ts (SINGLE SOURCE OF TRUTH)
 *    - If you need new types, ADD them to bookings.ts, DO NOT create duplicate files
 *
 * 2. NO DUPLICATE CODE ALLOWED
 *    - ALL transformations in /utils/bookingTransform.ts
 *    - ALL filtering logic in /utils/bookingFilters.ts
 *    - If you copy-paste the same function twice, YOU ARE DOING IT WRONG
 *
 * 3. FILTERING ARCHITECTURE
 *    - applyAllFilters() is the SINGLE entry point for all filtering
 *    - calculateTabCounts() uses SAME logic as filtering (never duplicate)
 *    - Tab counts MUST always match filtered data
 *    - If counts don't match data, you broke the single source of truth
 *
 * 4. DATA FETCHING
 *    - TanStack Query handles ALL caching automatically
 *    - staleTime: 0 means always check for fresh data
 *    - NEVER mix staleTime and refetchInterval (pick one strategy)
 *    - Date range filter triggers NEW API call (in queryKey)
 *
 * 5. AMOUNT CALCULATION
 *    - ALWAYS use venue_share (what vendor earns), NOT final_price
 *    - venue_share comes from DB as DECIMAL (string), must parseFloat()
 *    - See parseAmount() in bookingTransform.ts for correct implementation
 *
 * 6. RECURRING BOOKINGS
 *    - Display NEXT session for active bookings
 *    - Display LAST session for completed bookings
 *    - Filter by session date, NOT booking creation date
 *    - See transformRecurringBooking() for correct logic
 *
 * 7. NO DEAD CODE
 *    - Every button must have a handler
 *    - Every state variable must be used
 *    - No placeholder features in production
 *    - If it doesn't work, DELETE it or implement it properly
 *
 * 8. ERROR HANDLING
 *    - Show user-friendly messages, NOT console.log
 *    - Provide retry mechanism for failed requests
 *    - Never silently swallow errors
 *
 * 9. EXISTING FILES YOU MUST USE:
 *    - Types: /types/bookings.ts (DO NOT CREATE booking.types.ts or similar)
 *    - Transforms: /utils/bookingTransform.ts
 *    - Filters: /utils/bookingFilters.ts
 *    - Modal: /components/modals/BookingDetailsModal.tsx
 *
 * 10. PERFORMANCE
 *     - Use useMemo for expensive calculations
 *     - Dependency arrays must be PRECISE
 *     - Never recalculate on every render
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../utils/currency';
import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnFiltersState,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table';
import {
  Calendar,
  Clock,
  User,
  Search,
  CheckCircle,
  DollarSign,
  RefreshCw,
  MapPin,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { vendorApi } from '../services/api';
import { format } from 'date-fns';
import type { OneTimeBooking, RecurringBooking, BookingRow as BookingRowType, FilterState, TabType, ManualRecurringGroup } from '../types/bookings';
import { getStatusColor, getStatusIcon, getPaymentStatusColor } from '../utils/bookingHelpers';
import BookingDetailsModal from '../components/modals/BookingDetailsModal';
import RecurringBookingDetailsModal from '../components/modals/RecurringBookingDetailsModal';
import ManualRecurringBookingDetailsModal from '../components/modals/ManualRecurringBookingDetailsModal';
import CancellationReasonModal, { type ManualRefundData } from '../components/modals/CancellationReasonModal';
import RescheduleModal from '../components/modals/RescheduleModal';
import BlockTimeSlotModal, { type BlockSlotData } from '../components/modals/BlockTimeSlotModal';
import BlockedSlotDetailsModal from '../components/modals/BlockedSlotDetailsModal';
import AddManualBookingModal, { type ManualBookingData } from '../components/modals/AddManualBookingModal';
import EditManualSessionModal from '../components/modals/EditManualSessionModal';
import BulkGroupPriceModal from '../components/modals/BulkGroupPriceModal';
import Toast from '../components/Toast';
import DayViewTimeline from '../components/DayViewTimeline';
import DaySummary from '../components/DaySummary';
import {
  transformOneTimeBooking,
  transformRecurringBooking,
  calculateDuration,
  formatTimeWithoutSeconds,
  parseAmount,
  countCancelledSessions,
  groupManualRecurringBookings,
  transformManualRecurringGroup,
  generateCalendarReportCSV,
  generateDayReportCSV,
} from '../utils/bookingTransform';
import { useCourtsData } from '../hooks/useCourtsData';
import { useCacheInvalidation } from '../hooks/useCacheInvalidation';
import {
  applyAllFilters,
  calculateTabCounts,
  hasActiveFilters,
  getDefaultFilters,
  groupBookingsByDate,
  getMonthDays,
  getWeekDays,
  isToday,
  isCurrentMonth,
  getBookingTypeColor,
  isClosedDay,
  isBlackoutDate,
  consolidateBlockedSlots,
} from '../utils/bookingFilters';
import type { CourtSlotBooking } from '../types/bookings';

// ============================================================================
// TYPES
// ============================================================================

// Use BookingRowType from booking.types.ts
type BookingRow = BookingRowType;

interface StatsCard {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BookingsManagementNew: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { confirm, alert } = useConfirmation();
  const { can } = useVenueRole();
  const venue = user?.venues?.[0];
  const queryClient = useQueryClient();

  // Capability safety-net (staff-access): buttons are greyed out in the UI,
  // but guard the handlers too so no action slips through for a staff member
  // who lacks the capability. Returns false (and shows a message) when denied.
  const requireCapability = (capability: string): boolean => {
    if (can(capability)) return true;
    alert({
      title: 'Permission required',
      message: "You don't have permission to perform this action. Contact the venue owner for access.",
      variant: 'danger',
    });
    return false;
  };
  const { invalidateBookingRelated } = useCacheInvalidation();

  // Refresh user data on component mount to ensure operating hours are fresh
  React.useEffect(() => {
    refreshUser();
  }, []);

  // State
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [activeView, setActiveView] = useState<'list' | 'calendar' | 'availability'>('list');
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<BookingRow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Cancel/Reschedule modal state
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);
  const [refundEstimate, setRefundEstimate] = useState<any>(null);
  // For manual recurring groups - track the specific booking being cancelled/rescheduled
  const [selectedManualBookingId, setSelectedManualBookingId] = useState<number | null>(null);
  // For cancel all sessions in manual recurring group
  const [isCancelAllModalOpen, setIsCancelAllModalOpen] = useState(false);
  const [selectedRecurringGroupId, setSelectedRecurringGroupId] = useState<string | null>(null);

  // Block time slot modal state
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);

  // Blocked slot details modal state
  const [selectedBlockedSlot, setSelectedBlockedSlot] = useState<any>(null);
  const [isBlockedSlotModalOpen, setIsBlockedSlotModalOpen] = useState(false);

  // Manual booking modal state
  const [isManualBookingModalOpen, setIsManualBookingModalOpen] = useState(false);

  // Edit manual session modal state
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<any | null>(null);

  // Bulk group price modal state
  const [isBulkPriceModalOpen, setIsBulkPriceModalOpen] = useState(false);
  const [bulkPriceGroupId, setBulkPriceGroupId] = useState<string | null>(null);
  const [bulkPriceUpcomingCount, setBulkPriceUpcomingCount] = useState(0);
  const [bulkPriceTotalCount, setBulkPriceTotalCount] = useState(0);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'month' | 'week'>('month');

  // Day View state
  const [availabilityDate, setAvailabilityDate] = useState(new Date());

  // Filter state - single source of truth
  const [filters, setFilters] = useState<FilterState>(getDefaultFilters());

  // TanStack Table state
  // Default: newest first (stack order). Click DATE & TIME column header to sort by time.
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true }
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  // ============================================================================
  // CANCEL/RESCHEDULE HANDLERS
  // ============================================================================

  /**
   * Open cancel modal for recurring instance
   */
  const openCancelInstanceModal = (instanceId: number) => {
    // Close details modal first, then open cancel modal
    setIsModalOpen(false);
    setSelectedInstanceId(instanceId);
    setIsCancelModalOpen(true);
  };

  /**
   * Open cancel modal for one-time booking
   */
  const openCancelBookingModal = async (bookingId: number) => {
    if (!requireCapability('bookings.cancel')) return;
    // Close details modal first
    setIsModalOpen(false);

    // Store the specific booking ID (important for manual recurring groups)
    setSelectedManualBookingId(bookingId);

    // Check if this is a manual booking - they don't need refund estimates
    // since payment was received directly (cash, POS, or transfer)
    // Check both: regular manual bookings AND manual recurring groups
    const isManualRecurringGroup = selectedBooking?.type === 'manual_recurring';
    const bookingData = selectedBooking?.rawData as OneTimeBooking;
    const isManualBooking = isManualRecurringGroup || bookingData?.source === 'manual';

    if (isManualBooking) {
      // Skip refund estimate for manual bookings
      setRefundEstimate(null);
    } else {
      // Regular SPOTTS bookings - fetch refund estimate
      try {
        const estimate = await vendorApi.getRefundEstimate(bookingId);
        setRefundEstimate(estimate);
      } catch (error) {
        console.error('Failed to fetch refund estimate:', error);
        // Still open modal even if estimate fails
        setRefundEstimate(null);
      }
    }

    setIsCancelModalOpen(true);
  };

  /**
   * Open reschedule modal for one-time booking
   */
  const openRescheduleBookingModal = (bookingId: number) => {
    if (!requireCapability('bookings.edit')) return;
    // Close details modal first, then open reschedule modal
    setIsModalOpen(false);
    // Store the specific booking ID (important for manual recurring groups)
    setSelectedManualBookingId(bookingId);
    setIsRescheduleModalOpen(true);
  };

  /**
   * Open reschedule modal for recurring instance
   */
  const openRescheduleInstanceModal = (instanceId: number) => {
    if (!requireCapability('bookings.edit')) return;
    // Close details modal first, then open reschedule modal
    setIsModalOpen(false);
    setSelectedInstanceId(instanceId);
    setIsRescheduleModalOpen(true);
  };

  /**
   * Handle confirming cancellation from modal (recurring instance)
   */
  const handleConfirmCancelInstance = async (reason: string, notifyCustomer: boolean, _manualRefund?: ManualRefundData) => {
    if (!venue || !selectedInstanceId) return;

    try {
      const response = await vendorApi.cancelRecurringInstance(venue.id, selectedInstanceId, reason);

      if (response.success) {
        // Success - refresh all booking-related data across tabs (bookings, revenue, insights, etc.)
        await invalidateBookingRelated(venue.id);

        // Close modals
        setIsCancelModalOpen(false);
        setSelectedInstanceId(null);
        setIsModalOpen(false);
        setSelectedBooking(null);

        // Show success toast
        const refundAmount = response.data?.refund?.refund_amount || 0;
        setToast({
          type: 'success',
          message: `Session cancelled successfully!${refundAmount > 0 ? `\n\nRefund: ${CURRENCY_SYMBOL}${refundAmount.toLocaleString()}` : ''}${notifyCustomer ? '\n\nCustomer has been notified.' : ''}`,
        });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to cancel session';
      throw new Error(errorMessage);
    }
  };

  /**
   * Handle confirming cancellation from modal (one-time booking)
   */
  const handleConfirmCancelBooking = async (reason: string, notifyCustomer: boolean, manualRefund?: ManualRefundData, splitRefundData?: { type: 'none' | 'partial' | 'full'; amount?: number }) => {
    if (!venue || !selectedBooking) return;

    try {
      // Check if this is a manual booking (walk-in, phone, etc.)
      // Manual bookings don't go through refund processing since payment was received directly
      // Check both: regular manual bookings AND manual recurring groups
      const isManualRecurringGroup = selectedBooking.type === 'manual_recurring';
      const bookingData = selectedBooking.rawData as OneTimeBooking;
      const isManualBooking = isManualRecurringGroup || bookingData?.source === 'manual';

      // For manual recurring groups, use the specific booking ID stored earlier
      // For regular bookings, use selectedBooking.id
      const bookingIdToCancel = isManualRecurringGroup && selectedManualBookingId
        ? selectedManualBookingId
        : selectedBooking.id;

      let response;
      if (isManualBooking) {
        // Manual bookings use a different endpoint - includes refund tracking
        response = await vendorApi.cancelManualBooking(venue.id, bookingIdToCancel, reason, manualRefund, notifyCustomer);
      } else {
        // Regular SPOTTS bookings - may need refund processing
        const splitRefundGiven = splitRefundData ? splitRefundData.type !== 'none' : undefined;
        const splitRefundAmount = splitRefundData?.amount;
        response = await vendorApi.cancelBooking(venue.id, bookingIdToCancel, reason, splitRefundGiven, splitRefundAmount);
      }

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modals
      setIsCancelModalOpen(false);
      setIsModalOpen(false);
      setSelectedBooking(null);
      setSelectedManualBookingId(null);

      // Show success toast with appropriate message
      const successMessage = isManualBooking
        ? 'Booking cancelled successfully!\n\nThe time slot is now available for new bookings.'
        : `Booking cancelled successfully!${notifyCustomer ? '\n\nCustomer has been notified.' : ''}`;

      setToast({
        type: 'success',
        message: successMessage,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to cancel booking';
      throw new Error(errorMessage);
    }
  };

  /**
   * Handle deleting a manual booking entirely
   * Only for manual bookings (walk-in, phone, etc.)
   */
  const handleDeleteManualBooking = async (bookingId: number) => {
    if (!requireCapability('bookings.cancel')) return;
    if (!venue) return;

    try {
      await vendorApi.deleteManualBooking(venue.id, bookingId);

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modals
      setIsModalOpen(false);
      setSelectedBooking(null);

      // Show success toast
      setToast({
        type: 'success',
        message: 'Manual booking deleted successfully',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete booking';
      setToast({
        type: 'error',
        message: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  /**
   * Check in a booking via the detail modal
   */
  const handleCheckInBooking = async (bookingId: number) => {
    if (!requireCapability('checkin')) return;
    if (!venue) return;

    try {
      await vendorApi.checkInBooking(venue.id, bookingId);

      // Refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      setIsModalOpen(false);
      setSelectedBooking(null);

      setToast({
        type: 'success',
        message: 'Customer checked in successfully',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to check in';
      setToast({
        type: 'error',
        message: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  /**
   * Delete an entire manual recurring group (all sessions must be cancelled)
   */
  const handleDeleteRecurringGroup = async (groupId: string) => {
    if (!requireCapability('bookings.cancel')) return;
    if (!venue) return;

    // Show confirmation
    const confirmed = await confirm({
      title: 'Delete Recurring Pack?',
      message: 'This will permanently delete all sessions in this recurring booking pack. This action cannot be undone.',
      confirmLabel: 'Delete Pack',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      const result = await vendorApi.deleteRecurringGroup(venue.id, groupId);

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modals
      setIsModalOpen(false);
      setSelectedBooking(null);

      // Show success toast
      setToast({
        type: 'success',
        message: result.message || `Deleted ${result.deleted_count} session(s) from the recurring pack`,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete recurring pack';
      setToast({
        type: 'error',
        message: errorMessage,
      });
    }
  };

  /**
   * Open cancel all sessions modal for manual recurring group
   */
  const openCancelAllSessionsModal = (groupId: string) => {
    if (!requireCapability('bookings.cancel')) return;
    // Close details modal first
    setIsModalOpen(false);
    setSelectedRecurringGroupId(groupId);
    setIsCancelAllModalOpen(true);
  };

  /**
   * Handle confirming cancel all sessions in a manual recurring group
   */
  const handleConfirmCancelAllSessions = async (reason: string, notifyCustomer: boolean, manualRefund?: ManualRefundData) => {
    if (!venue || !selectedRecurringGroupId) return;

    try {
      const result = await vendorApi.cancelRecurringGroup(venue.id, selectedRecurringGroupId, reason, manualRefund, notifyCustomer);

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modals
      setIsCancelAllModalOpen(false);
      setSelectedRecurringGroupId(null);
      setSelectedBooking(null);

      // Show success toast with details
      setToast({
        type: 'success',
        message: result.message || `Successfully cancelled ${result.cancelled_count} session(s)`,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to cancel all sessions';
      setToast({
        type: 'error',
        message: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  /**
   * Open edit session modal for a manual recurring session
   */
  const openEditSessionModal = (bookingId: number) => {
    if (!selectedBooking || selectedBooking.type !== 'manual_recurring') return;
    const group = selectedBooking.rawData as ManualRecurringGroup;
    const session = group.bookings.find((b) => b.id === bookingId);
    if (!session) return;
    setEditingSession({ ...session, customer_email: group.customer_email });
    setIsEditSessionModalOpen(true);
  };

  /**
   * Handle confirming an edit to a manual session (called by EditManualSessionModal)
   */
  const handleConfirmEditSession = async (
    bookingId: number,
    data: {
      price?: number;
      notes?: string;
      customer_name?: string;
      customer_phone?: string;
      customer_email?: string;
      notify_customer: boolean;
    }
  ) => {
    if (!venue) return;
    // Throws on error — EditManualSessionModal catches and shows inline
    await vendorApi.updateManualBooking(venue.id, bookingId, data);
    await invalidateBookingRelated(venue.id);
    setIsEditSessionModalOpen(false);
    setEditingSession(null);
    setToast({ type: 'success', message: 'Session updated successfully' });
  };

  const openBulkPriceModal = (groupId: string, upcomingCount: number, totalCount: number) => {
    setBulkPriceGroupId(groupId);
    setBulkPriceUpcomingCount(upcomingCount);
    setBulkPriceTotalCount(totalCount);
    setIsBulkPriceModalOpen(true);
  };

  const handleConfirmBulkPrice = async (
    groupId: string,
    data: { price: number; notes: string; include_past: boolean }
  ) => {
    if (!venue) return;
    // Throws on error — BulkGroupPriceModal catches and shows inline
    await vendorApi.bulkUpdateGroupPrice(venue.id, groupId, data);
    await invalidateBookingRelated(venue.id);
    setToast({ type: 'success', message: 'Group price updated successfully' });
  };

  /**
   * Handle confirming reschedule from modal
   */
  const handleConfirmReschedule = async (data: {
    new_date: string;
    new_start_time: string;
    new_end_time: string;
    reason?: string;
    notify_customer: boolean;
  }) => {
    if (!venue || !selectedBooking) return;

    try {
      let response;

      // If rescheduling a recurring instance, use the instance endpoint
      if (selectedInstanceId && selectedBooking.type === 'recurring') {
        response = await vendorApi.rescheduleRecurringInstance(venue.id, selectedInstanceId, data);
      } else {
        // For manual recurring groups, use the specific booking ID stored earlier
        // For regular bookings, use selectedBooking.id
        const isManualRecurringGroup = selectedBooking.type === 'manual_recurring';
        const bookingIdToReschedule = isManualRecurringGroup && selectedManualBookingId
          ? selectedManualBookingId
          : selectedBooking.id;

        // Reschedule the one-time booking (or manual booking from recurring group)
        response = await vendorApi.rescheduleBooking(venue.id, bookingIdToReschedule, data);
      }

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modals
      setIsRescheduleModalOpen(false);
      setIsModalOpen(false);
      setSelectedBooking(null);
      setSelectedInstanceId(null); // Clear instance selection
      setSelectedManualBookingId(null); // Clear manual booking selection

      // Show success toast
      const bookingType = selectedInstanceId ? 'Session' : 'Booking';
      setToast({
        type: 'success',
        message: `${bookingType} rescheduled successfully!\n\nNew date: ${data.new_date}\nNew time: ${data.new_start_time} - ${data.new_end_time}${data.notify_customer ? '\n\nCustomer has been notified.' : ''}`,
      });
    } catch (error: any) {
      // Parse backend error message
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to reschedule booking';

      // Show specific error for conflicts
      if (errorMessage.includes('not available') || errorMessage.includes('Another booking exists')) {
        throw new Error('This time slot is not available. Another booking already exists at this time. Please choose a different time.');
      }

      throw new Error(errorMessage);
    }
  };

  /**
   * Handle blocking a time slot
   */
  const handleBlockSlot = async (blockData: BlockSlotData) => {
    if (!requireCapability('slots.manage')) return;
    if (!venue) return;

    try {
      await vendorApi.blockTimeSlot(venue.id, blockData.court_id, blockData);

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modal
      setIsBlockModalOpen(false);

      // Show success toast
      setToast({
        type: 'success',
        message: `Time slot blocked successfully!\n\nDate: ${blockData.date}\nTime: ${blockData.start_time} - ${blockData.end_time}`,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to block time slot';
      throw new Error(errorMessage);
    }
  };

  /**
   * Handle unblocking a blocked time slot
   */
  const handleUnblockSlot = async (blockId: number) => {
    if (!requireCapability('slots.manage')) return;
    if (!venue || !selectedBlockedSlot) return;

    try {
      await vendorApi.unblockTimeSlot(venue.id, selectedBlockedSlot.court_id, blockId);

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modal
      setIsBlockedSlotModalOpen(false);
      setSelectedBlockedSlot(null);

      // Show success toast
      setToast({
        type: 'success',
        message: 'Time slot unblocked successfully!',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to unblock time slot';
      setToast({
        type: 'error',
        message: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  /**
   * Handle creating a manual booking
   */
  const handleCreateManualBooking = async (bookingData: ManualBookingData) => {
    if (!venue) return;

    try {
      const result = await vendorApi.createManualBooking(venue.id, bookingData);

      // Success - refresh all booking-related data across tabs
      await invalidateBookingRelated(venue.id);

      // Close modal
      setIsManualBookingModalOpen(false);

      // Show success toast
      setToast({
        type: 'success',
        message: `Manual booking created successfully!\n\nBooking Ref: ${result.booking?.booking_reference || 'N/A'}\nCustomer: ${bookingData.customer_name}\nDate: ${bookingData.booking_date}\nTime: ${bookingData.start_time} - ${bookingData.end_time}`,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create manual booking';
      throw new Error(errorMessage);
    }
  };

  // ============================================================================
  // DATA FETCHING WITH TANSTACK QUERY (NO MANUAL CACHING!)
  // ============================================================================

  // Fetch courts for availability view, manual booking modal, and block time slot modal
  // Use the shared useCourtsData hook to ensure consistent caching across all pages
  const { data: courtsData, isPending: isCourtsLoading } = useCourtsData(venue?.id);
  const courts = courtsData?.courts;

  // Fetch schedule data for availability view
  // Load upfront so view switching is instant
  const { data: scheduleData, isPending: isScheduleLoading } = useQuery({
    queryKey: ['schedule', venue?.id, format(availabilityDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      if (!venue) throw new Error('No venue selected');
      const dateStr = format(availabilityDate, 'yyyy-MM-dd');
      return vendorApi.getSchedule(venue.id, {
        date: dateStr,
        start_date: dateStr,
        end_date: dateStr,
      });
    },
    enabled: !!venue, // Load regardless of active view for instant switching
    staleTime: 15 * 1000, // Consider fresh for 15 seconds
    refetchInterval: activeView === 'availability' ? 30 * 1000 : false, // Only poll when viewing
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData, // Keep showing old data while refetching
  });

  // Fetch blackout dates for calendar view
  // Load upfront so view switching is instant
  const { data: blackoutDates } = useQuery({
    queryKey: ['blackoutDates', venue?.id],
    queryFn: async () => {
      if (!venue) throw new Error('No venue selected');
      return vendorApi.getBlackoutDates(venue.id);
    },
    // Blackout dates are a slots.manage endpoint; staff without it just don't
    // see blackout markers (rather than a 403 storm on the bookings view).
    enabled: !!venue && can('slots.manage'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (blackout dates don't change often)
    placeholderData: (previousData) => previousData,
  });

  const { data: bookingsData, isPending, error } = useQuery({
    queryKey: ['bookings', venue?.id, filters.dateRange],
    queryFn: async () => {
      if (!venue) throw new Error('No venue selected');

      const today = new Date();
      const startDate = new Date(today);
      const endDate = new Date(today);

      // Calculate date range for API call
      if (filters.dateRange === 'all') {
        startDate.setFullYear(2020);
        endDate.setFullYear(endDate.getFullYear() + 1);
      } else {
        const days = parseInt(filters.dateRange);
        startDate.setDate(startDate.getDate() - days);
        endDate.setDate(endDate.getDate() + days);
      }

      const formatApiDate = (date: Date): string => {
        return format(date, 'yyyy-MM-dd');
      };

      // Fetch data in parallel (including blocked slots for calendar view)
      const [allBookings, allRecurringBookings, scheduleData] = await Promise.all([
        vendorApi.getBookings(venue.id, {
          start_date: formatApiDate(startDate),
          end_date: formatApiDate(endDate),
        }),
        vendorApi.getRecurringBookings(venue.id),
        vendorApi.getSchedule(venue.id, {
          start_date: formatApiDate(startDate),
          end_date: formatApiDate(endDate),
        }),
      ]);

      // Include paid bookings AND trusted reservations awaiting payment
      const paidBookings = allBookings.filter(
        (b) =>
          b.payment_status === 'successful' ||
          b.payment_status === 'refunded' ||
          b.is_trusted_reservation === true
      );

      // NOTE: payment_status standardized to 'successful' (migration 2026_02_04_180000)
      const paidRecurringBookings = allRecurringBookings.filter(
        (rb) => rb.payment_status === 'successful' || rb.payment_status === 'refunded'
      );

      // Extract blocked slots from schedule data
      const blockedSlots = scheduleData?.blocked_slots || [];
      // Cancelled partner blocks (released with reason='cancelled')
      const cancelledPartnerBlocks = scheduleData?.cancelled_partner_blocks || [];

      return {
        bookings: paidBookings,
        recurringBookings: paidRecurringBookings,
        blockedSlots: blockedSlots,
        cancelledPartnerBlocks: cancelledPartnerBlocks,
      };
    },
    enabled: !!venue,
    staleTime: 15 * 1000, // Consider fresh for 15 seconds
    refetchInterval: 45 * 1000, // Auto-refresh every 45 seconds for calendar/list views
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnMount: true, // Refetch when component mounts
    placeholderData: (previousData) => previousData, // Keep showing old data while refetching
  });

  // Transform raw API data to table rows (type-safe, no 'any')
  const tableData: BookingRow[] = useMemo(() => {
    if (!bookingsData) return [];

    const today = format(new Date(), 'yyyy-MM-dd');
    const rows: BookingRow[] = [];

    // Separate manual bookings for grouping
    const manualBookings = bookingsData.bookings.filter((b) => b.source === 'manual');
    const spottsBookings = bookingsData.bookings.filter((b) => b.source !== 'manual');

    // Group manual recurring bookings by recurring_group_id
    const { groups: manualRecurringGroups, standalone: standaloneManualBookings } =
      groupManualRecurringBookings(manualBookings);

    // Transform standalone manual bookings (not part of a recurring group)
    standaloneManualBookings.forEach((booking) => {
      rows.push(transformOneTimeBooking(booking));
    });

    // Transform manual recurring groups - show ONE row per group
    manualRecurringGroups.forEach((group) => {
      const row = transformManualRecurringGroup(group, today);
      if (row) rows.push(row);
    });

    // Transform spotts one-time bookings
    spottsBookings.forEach((booking) => {
      rows.push(transformOneTimeBooking(booking));
    });

    // Transform SPOTTS recurring bookings - show next/last session for table
    bookingsData.recurringBookings.forEach((recurring) => {
      const row = transformRecurringBooking(recurring, today);
      if (row) rows.push(row);
    });

    // Transform blocked slots for calendar view
    if (bookingsData.blockedSlots && Array.isArray(bookingsData.blockedSlots)) {
      bookingsData.blockedSlots.forEach((block: any) => {
        // Extract date from datetime string (handle both 'YYYY-MM-DD' and ISO datetime formats)
        const dateStr = block.date.includes('T')
          ? block.date.split('T')[0]
          : block.date;

        const isPartnerBlock = block.is_partner_block || block.reason === 'third_party';
        // Calculate duration from start_time and end_time
        const blockDuration = calculateDuration(block.start_time, block.end_time);
        rows.push({
          id: block.id || 0, // Use numeric ID from block
          reference: isPartnerBlock ? (block.block_reference || `EXT-${block.id}`) : `BLOCKED-${block.id}`,
          sport: 'N/A',
          customer: isPartnerBlock
            ? (block.partner_name ? `${block.partner_name}` : 'External Partner')
            : (block.customer_name || 'Blocked Slot'),
          court: block.court_name || 'Unknown Court',
          date: dateStr, // Use parsed date (YYYY-MM-DD format)
          time: `${formatTimeWithoutSeconds(block.start_time)} - ${formatTimeWithoutSeconds(block.end_time)}`,
          duration: blockDuration,
          amount: 0,
          // Partner blocks show as "external", regular blocks as "blocked"
          status: isPartnerBlock ? ('external' as any) : ('blocked' as any),
          paymentStatus: 'n/a' as any,
          type: isPartnerBlock ? ('external' as any) : ('blocked' as any),
          source: isPartnerBlock ? 'external' : 'manual', // External = partner API, manual = vendor blocked
          createdAt: block.created_at || '',
          rawData: block,
        });
      });
    }

    // Transform cancelled partner blocks (released external reservations)
    // These show in the Cancelled tab for vendor visibility
    if (bookingsData.cancelledPartnerBlocks && Array.isArray(bookingsData.cancelledPartnerBlocks)) {
      bookingsData.cancelledPartnerBlocks.forEach((block: any) => {
        const dateStr = block.date.includes('T')
          ? block.date.split('T')[0]
          : block.date;

        const blockDuration = calculateDuration(block.start_time, block.end_time);
        rows.push({
          id: block.id || 0,
          reference: block.block_reference || `EXT-${block.id}`,
          sport: 'N/A',
          customer: block.partner_name ? `${block.partner_name}` : 'External Partner',
          court: block.court_name || 'Unknown Court',
          date: dateStr,
          time: `${formatTimeWithoutSeconds(block.start_time)} - ${formatTimeWithoutSeconds(block.end_time)}`,
          duration: blockDuration,
          amount: 0,
          // Show as cancelled so it appears in Cancelled tab
          status: 'cancelled' as any,
          paymentStatus: 'n/a' as any,
          type: 'external' as any, // Still external type for styling
          source: 'external',
          createdAt: block.created_at || '',
          rawData: { ...block, is_cancelled_partner_block: true },
        });
      });
    }

    return rows;
  }, [bookingsData]);

  // Apply all filters using single source of truth
  const filteredData = useMemo(
    () => applyAllFilters(tableData, activeTab, filters),
    [tableData, activeTab, filters]
  );

  // Reset to page 1 when tab or filters change
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [activeTab, filters, globalFilter]);

  // ============================================================================
  // FILTER DROPDOWN OPTIONS
  // Show ALL available options, not just those with existing bookings
  // ============================================================================

  // Sports filter: Use venue's sports list (all sports the venue offers)
  const availableSports = useMemo(() => {
    const sports = venue?.sports?.map((sport: any) => sport.name) || [];
    return ['all', ...sports.sort()];
  }, [venue?.sports]);

  // Courts filter: Use the fetched courts data (all courts at the venue)
  // Defensive check: ensure courts is actually an array (not an object or other type)
  // Deduplicate court names to avoid React key warnings (e.g., multiple "Half A" courts)
  const availableCourts = useMemo(() => {
    const courtsArray = Array.isArray(courts) ? courts : [];
    const courtMap = new Map<number, any>();
    courtsArray.forEach((c: any) => courtMap.set(c.id, c));

    const options: { value: string; label: string }[] = [{ value: 'all', label: 'All Courts' }];
    const seen = new Set<string>();
    const sortedCourts = [...courtsArray].sort((a: any, b: any) => a.name.localeCompare(b.name));

    sortedCourts.forEach((court: any) => {
      if (seen.has(court.name)) return;
      seen.add(court.name);

      if (court.parent_court_id) {
        // Child court — prefix with parent name
        const parent = courtMap.get(court.parent_court_id);
        options.push({
          value: court.name,
          label: parent ? `${parent.name} › ${court.name}` : court.name,
        });
      } else {
        const hasChildren = courtsArray.some((c: any) => c.parent_court_id === court.id);
        options.push({
          value: court.name,
          label: hasChildren ? `${court.name} (Full)` : court.name,
        });
      }
    });

    return options;
  }, [courts]);

  // Status filter: Show all possible booking statuses (predefined list)
  const availableStatuses = useMemo(() => {
    return ['all', 'confirmed', 'pending_payment', 'completed', 'cancelled', 'blocked', 'external'];
  }, []);

  // Calculate stats - refund-aware and source-aware
  const stats: StatsCard[] = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');

    // Get today's bookings BY SESSION DATE (for booking count and completed count)
    const allTodayBookings = tableData.filter(
      (row) => row.date && row.date === today && (row.type as any) !== 'blocked' && (row.type as any) !== 'external'
    );

    // Active bookings for today (exclude cancelled and refunded)
    const activeTodayBookings = allTodayBookings.filter(
      (row) => row.status !== 'cancelled' && row.paymentStatus !== 'refunded'
    );

    const completedToday = allTodayBookings.filter((row) => row.status === 'completed');
    const recurringActive = tableData.filter((row) => (row.type === 'recurring' || row.type === 'manual_recurring') && row.status !== 'completed');

    // Revenue bookings: filter by PAYMENT date (paid_at), not session date.
    // A booking paid today counts toward today's revenue regardless of when the session is.
    const todayRevenueBookings = tableData.filter((row) => {
      if (row.type === 'blocked' || row.type === 'external') return false;

      if (row.source === 'manual') {
        // Manual bookings: use created_at (manual = paid on creation)
        return row.createdAt && row.createdAt.startsWith(today);
      } else {
        // Spotts bookings: use paid_at (payment confirmation date)
        const paidAt = (row.rawData as any)?.paid_at;
        if (paidAt) {
          return paidAt.startsWith(today);
        }
        // Fallback to booking_date for entries without paid_at (unpaid/legacy)
        return row.date && row.date === today;
      }
    });

    // Calculate today's revenue (refund-aware)
    // For Spotts Bookings:
    //   - payment_status = 'successful' → Full venue_share counted
    //   - payment_status = 'refunded' → venue_share - (refund_amount × venue_share/final_price)
    // For Manual Single Bookings:
    //   - Active statuses → Full venue_share
    //   - Cancelled with refund_type → venue_share - refund_amount
    //   - Cancelled without refund_type → Excluded (cancelled before refund tracking)
    // For Manual Recurring Groups:
    //   - Sum individual session revenues from group.bookings[]
    const todayRevenue = todayRevenueBookings.reduce((sum, row) => {
      const rawData = row.rawData as any;

      if (row.source === 'manual') {
        if (row.type === 'manual_recurring' && rawData?.bookings) {
          // Manual recurring group: sum per-session revenue (handles partial cancellations)
          let groupRevenue = 0;
          for (const booking of rawData.bookings) {
            const sessionShare = parseFloat(booking.venue_share || booking.final_price || 0);
            if (booking.status === 'cancelled') {
              const refundAmt = parseFloat(booking.refund_amount || 0);
              if (booking.refund_type && refundAmt > 0) {
                // Cancelled with refund: venue kept (venue_share - refund_amount)
                groupRevenue += Math.max(0, sessionShare - refundAmt);
              } else if (booking.refund_type === 'none') {
                // Cancelled but no refund given: vendor kept the money
                groupRevenue += sessionShare;
              }
              // No refund_type = excluded (cancelled before refund tracking)
            } else {
              const activeStatuses = ['paid', 'confirmed', 'active', 'completed', 'scheduled'];
              if (activeStatuses.includes(booking.status)) {
                groupRevenue += sessionShare;
              }
            }
          }
          return sum + groupRevenue;
        } else {
          // Manual single booking
          const venueShare = parseFloat(rawData?.venue_share || row.amount || 0);
          if (row.status === 'cancelled') {
            const refundAmt = parseFloat(rawData?.refund_amount || 0);
            if (rawData?.refund_type && refundAmt > 0) {
              // Cancelled with refund: venue kept (venue_share - refund_amount)
              return sum + Math.max(0, venueShare - refundAmt);
            } else if (rawData?.refund_type === 'none') {
              // Cancelled but no refund: vendor kept the money
              return sum + venueShare;
            }
            return sum; // Excluded
          }
          const activeStatuses = ['paid', 'confirmed', 'active', 'completed', 'scheduled'];
          if (activeStatuses.includes(row.status)) {
            return sum + venueShare;
          }
          return sum;
        }
      } else {
        // Spotts bookings: refund-aware calculation
        const venueShare = parseFloat(rawData?.venue_share || row.amount || 0);
        if (row.paymentStatus === 'successful') {
          return sum + venueShare;
        } else if (row.paymentStatus === 'refunded') {
          // Calculate: venue_share - (refund_amount × venue_share/final_price)
          const refundAmount = parseFloat(rawData?.refund_amount || 0);
          const finalPrice = parseFloat(rawData?.final_price || 1);
          const refundDeduction = (refundAmount * venueShare) / finalPrice;
          return sum + Math.max(0, venueShare - refundDeduction);
        }
        return sum; // Exclude pending/failed payments
      }
    }, 0);

    return [
      {
        label: "Today's Bookings",
        value: activeTodayBookings.length,
        icon: Calendar,
        color: 'bg-blue-50 text-blue-600',
      },
      {
        label: 'Completed Today',
        value: completedToday.length,
        icon: CheckCircle,
        color: 'bg-green-50 text-green-600',
      },
      {
        label: 'Active Recurring',
        value: recurringActive.length,
        icon: RefreshCw,
        color: 'bg-purple-50 text-purple-600',
      },
      {
        label: "Today's Revenue",
        value: `${CURRENCY_SYMBOL}${Math.round(todayRevenue).toLocaleString()}`,
        icon: DollarSign,
        color: 'bg-orange-50 text-orange-600',
      },
    ];
  }, [tableData]);

  // ============================================================================
  // CALENDAR DATA - Expand all recurring instances for calendar view
  // ============================================================================
  const calendarData: BookingRow[] = useMemo(() => {
    if (!bookingsData) return [];

    const rows: BookingRow[] = [];

    // Add all one-time bookings
    bookingsData.bookings.forEach((booking) => {
      rows.push(transformOneTimeBooking(booking));
    });

    // Expand ALL recurring instances for calendar (not just next/last)
    bookingsData.recurringBookings.forEach((recurring) => {
      if (recurring.instances && Array.isArray(recurring.instances)) {
        // Count cancelled sessions for this booking (for badge display)
        const cancelledCount = countCancelledSessions(recurring.instances);

        recurring.instances.forEach((instance: any) => {
          const duration = calculateDuration(
            instance.scheduled_start_time,
            instance.scheduled_end_time
          );

          // Determine status: instance cancelled OR entire booking cancelled
          let instanceStatus = instance.status;
          if (instance.status === 'cancelled') {
            instanceStatus = 'cancelled';
          } else if (recurring.status === 'cancelled') {
            instanceStatus = 'cancelled';
          }

          rows.push({
            id: instance.id,
            reference: `${recurring.booking_reference}-${instance.instance_number}`,
            sport: recurring.court?.sport?.name || 'N/A',
            customer: recurring.customer?.name || recurring.user?.name || 'Unknown',
            court: recurring.court?.name || 'N/A',
            date: instance.scheduled_date,
            time: `${formatTimeWithoutSeconds(instance.scheduled_start_time)} - ${formatTimeWithoutSeconds(instance.scheduled_end_time)}`,
            duration,
            amount: parseAmount(recurring),
            status: instanceStatus,
            paymentStatus: recurring.payment_status,
            recurringInfo: `Session ${instance.instance_number} of ${recurring.total_sessions || 0}`,
            cancelledSessionsCount: cancelledCount,  // Add badge count
            type: 'recurring' as const,
            source: (recurring as any).source || 'spotts',
            createdAt: recurring.created_at || '',
            rawData: recurring,
          });
        });
      }
    });

    // Add blocked slots (including partner/external blocks)
    if (bookingsData.blockedSlots && Array.isArray(bookingsData.blockedSlots)) {
      bookingsData.blockedSlots.forEach((block: any) => {
        const dateStr = block.date.includes('T')
          ? block.date.split('T')[0]
          : block.date;

        const isPartnerBlock = block.is_partner_block || block.reason === 'third_party';
        const blockDuration = calculateDuration(block.start_time, block.end_time);
        rows.push({
          id: block.id || 0,
          reference: isPartnerBlock ? (block.block_reference || `EXT-${block.id}`) : `BLOCKED-${block.id}`,
          sport: 'N/A',
          customer: isPartnerBlock
            ? (block.partner_name ? `${block.partner_name}` : 'External Partner')
            : (block.customer_name || 'Blocked Slot'),
          court: block.court_name || 'Unknown Court',
          date: dateStr,
          time: `${formatTimeWithoutSeconds(block.start_time)} - ${formatTimeWithoutSeconds(block.end_time)}`,
          duration: blockDuration,
          amount: 0,
          status: isPartnerBlock ? ('external' as any) : ('blocked' as any),
          paymentStatus: 'n/a' as any,
          type: isPartnerBlock ? ('external' as any) : ('blocked' as any),
          source: isPartnerBlock ? 'external' : 'manual',
          createdAt: block.created_at || '',
          rawData: block,
        });
      });
    }

    return rows;
  }, [bookingsData]);

  // ============================================================================
  // FILTERED CALENDAR DATA - Apply same filters as list view
  // ============================================================================
  const filteredCalendarData = useMemo(() => {
    // Apply filters to calendar data (sport, court, status, source)
    // Note: We don't apply tab filter to calendar as it shows all bookings
    // But we do apply the dropdown filters (sport, court, status, source)
    let filtered = calendarData;

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter((row) => row.status === filters.status);
    }

    // Apply sport filter
    if (filters.sport !== 'all') {
      filtered = filtered.filter((row) => row.sport === filters.sport);
    }

    // Apply court filter
    if (filters.court !== 'all') {
      filtered = filtered.filter((row) => row.court === filters.court);
    }

    // Apply source filter (Manual/Spotts/All)
    if (filters.source !== 'all') {
      filtered = filtered.filter((row) => {
        const rawData = row.rawData as any;
        const source = rawData?.source || 'spotts'; // Default to 'spotts' for app bookings
        return source === filters.source;
      });
    }

    return filtered;
  }, [calendarData, filters.status, filters.sport, filters.court, filters.source]);

  // ============================================================================
  // TIMELINE BOOKINGS - For DayViewTimeline and DaySummary
  // ============================================================================
  const timelineBookings = useMemo(() => {
    const bookings: any[] = [];

    // Add one-time bookings
    scheduleData?.one_time_bookings?.forEach((booking: any) => {
      const isManual = booking.source === 'manual';
      bookings.push({
        id: booking.id,
        court_id: booking.court_id,
        booking_reference: booking.booking_reference,
        start_time: booking.start_time?.substring(0, 5) || '00:00',
        end_time: booking.end_time?.substring(0, 5) || '00:00',
        customer_name: booking.customer_name || booking.customer?.name || 'N/A',
        sport_name: booking.sport_name,
        type: booking.status === 'cancelled' ? 'cancelled' : (isManual ? 'manual' : 'spotts'),
        status: booking.status,
        rawData: booking,
      });
    });

    // Add recurring instances
    scheduleData?.recurring_instances?.forEach((instance: any) => {
      const rb = instance.recurringBooking || instance;
      bookings.push({
        id: instance.id,
        court_id: rb.court_id || instance.court_id,
        booking_reference: rb.booking_reference,
        start_time: instance.scheduled_start_time?.substring(0, 5) || instance.start_time?.substring(0, 5) || '00:00',
        end_time: instance.scheduled_end_time?.substring(0, 5) || instance.end_time?.substring(0, 5) || '00:00',
        customer_name: rb.customer_name || rb.user?.name || 'N/A',
        sport_name: rb.court?.sport?.name,
        type: instance.status === 'cancelled' ? 'cancelled' : 'recurring',
        status: instance.status,
        rawData: instance,
      });
    });

    // Add blocked slots (including external/partner reservations)
    scheduleData?.blocked_slots?.forEach((block: any) => {
      const isPartnerBlock = block.is_partner_block || block.reason === 'third_party';
      bookings.push({
        id: block.id,
        court_id: block.court_id,
        booking_reference: isPartnerBlock ? (block.block_reference || `EXT-${block.id}`) : `BLOCKED-${block.id}`,
        start_time: block.start_time?.substring(0, 5) || '00:00',
        end_time: block.end_time?.substring(0, 5) || '00:00',
        customer_name: isPartnerBlock
          ? (block.partner_name || 'External Partner')
          : (block.customer_name || 'Blocked'),
        sport_name: isPartnerBlock ? 'External Reservation' : (block.reason || 'Blocked Slot'),
        type: isPartnerBlock ? 'external' : 'blocked',
        status: isPartnerBlock ? 'external' : 'blocked',
        rawData: block,
      });
    });

    return bookings;
  }, [scheduleData]);

  // Filtered timeline bookings (apply filters)
  const filteredTimelineBookings = useMemo(() => {
    return timelineBookings.filter((b) => {
      // Sport filter
      if (filters.sport !== 'all' && b.sport_name !== filters.sport) return false;
      // Court filter - match by court name from rawData
      if (filters.court !== 'all') {
        const courtName = b.rawData?.court_name || b.rawData?.court?.name || '';
        if (courtName !== filters.court) return false;
      }
      // Source filter
      if (filters.source !== 'all') {
        if (filters.source === 'manual' && b.type !== 'manual') return false;
        if (filters.source === 'spotts' && b.type !== 'spotts') return false;
      }
      return true;
    });
  }, [timelineBookings, filters.sport, filters.court, filters.source]);

  // Courts to show in Day View Timeline — narrows to selected court when filter is active
  const timelineCourts = useMemo(() => {
    const courtsArray = Array.isArray(courts) ? courts : [];
    if (filters.court === 'all') return courtsArray;
    const selected = courtsArray.find((c: any) => c.name === filters.court);
    if (!selected) return courtsArray;
    if (selected.parent_court_id) {
      // Child court selected — show parent + just this child
      const parent = courtsArray.find((c: any) => c.id === selected.parent_court_id);
      return parent ? [parent, selected] : [selected];
    }
    // Parent or standalone court selected — show it + all its children
    const children = courtsArray.filter((c: any) => c.parent_court_id === selected.id);
    return [selected, ...children];
  }, [courts, filters.court]);

  // ============================================================================
  // TANSTACK TABLE CONFIGURATION
  // ============================================================================

  const columnHelper = createColumnHelper<BookingRow>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('reference', {
        header: 'REFERENCE',
        cell: (info) => {
          const ref = info.getValue();
          const isLong = ref.length > 12;
          return (
            <div>
              <p className={`font-bold text-gray-900 whitespace-nowrap ${isLong ? 'text-xs' : 'text-sm'}`}>{ref}</p>
              <p className="text-xs text-gray-500">{info.row.original.sport}</p>
            </div>
          );
        },
      }),
      columnHelper.accessor('customer', {
        header: 'CUSTOMER',
        cell: (info) => (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <span className="font-semibold text-gray-900 text-sm">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('court', {
        header: 'COURT',
        cell: (info) => (
          <div className="flex items-center space-x-1 text-gray-700 text-sm">
            <MapPin className="w-3 h-3 text-gray-500" />
            <span>{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('date', {
        header: 'DATE & TIME',
        cell: (info) => {
          const dateValue = info.getValue();
          let formattedDate = 'N/A';

          try {
            if (dateValue && dateValue !== 'N/A') {
              const parsedDate = new Date(dateValue);
              if (!isNaN(parsedDate.getTime())) {
                formattedDate = format(parsedDate, 'MMM dd, yyyy');
              }
            }
          } catch (error) {
            console.error('Date parsing error:', error, dateValue);
          }

          return (
            <div>
              <p className="font-semibold text-gray-900 text-sm">{formattedDate}</p>
              <p className="text-xs text-gray-600">{info.row.original.time}</p>
            </div>
          );
        },
      }),
      columnHelper.accessor('duration', {
        header: 'DURATION',
        cell: (info) => (
          <span className="text-gray-700 text-sm font-medium">{info.getValue()} mins</span>
        ),
      }),
      columnHelper.accessor('amount', {
        header: 'AMOUNT',
        cell: (info) => {
          const row = info.row.original;
          // External and blocked bookings have no payment data - show dash
          if (row.type === 'external' || row.type === 'blocked') {
            return <p className="font-black text-gray-400 text-base">—</p>;
          }
          return <p className="font-black text-gray-900 text-base">{CURRENCY_SYMBOL}{Math.round(Number(info.getValue())).toLocaleString()}</p>;
        },
      }),
      columnHelper.accessor('source', {
        header: 'SOURCE',
        cell: (info) => {
          const row = info.row.original;
          // Determine source: check row.source first, then rawData.source, then infer from type
          let source = row.source || (row.rawData as any)?.source;
          if (!source) {
            // Infer from type if not explicitly set
            if (row.type === 'external') source = 'external';
            else if (row.type === 'blocked') source = 'manual';
            else if (row.type === 'manual_recurring') source = 'manual';
            else source = 'spotts';
          }

          const sourceConfig: Record<string, { label: string; color: string; icon: string }> = {
            spotts: { label: 'SPOTTS', color: 'bg-blue-100 text-blue-800', icon: '📱' },
            manual: { label: 'Manual', color: 'bg-green-100 text-green-800', icon: '✍️' },
            external: { label: 'External', color: 'bg-teal-100 text-teal-800', icon: '🔗' },
          };

          const config = sourceConfig[source] || sourceConfig.spotts;

          return (
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${config.color}`}
            >
              <span>{config.icon}</span>
              <span>{config.label}</span>
            </span>
          );
        },
      }),
      columnHelper.accessor('status', {
        header: 'STATUS',
        cell: (info) => {
          const row = info.row.original;
          const isRecurring = row.type === 'recurring' || row.type === 'manual_recurring';
          const isManualRecurring = row.type === 'manual_recurring';
          const cancelledCount = row.cancelledSessionsCount || 0;
          const totalSessions = isManualRecurring
            ? (row.rawData as ManualRecurringGroup)?.total_sessions
            : (row.rawData as RecurringBooking)?.total_sessions || 0;

          // Extract session number from recurringInfo (e.g., "Session 2 of 3" -> "2/3")
          let sessionInfo = '';
          if (row.recurringInfo && isRecurring) {
            const match = row.recurringInfo.match(/Session (\d+) of (\d+)/);
            if (match) {
              sessionInfo = `${match[1]}/${match[2]}`;
            } else if (row.recurringInfo.includes('Completed')) {
              sessionInfo = `✓ ${totalSessions}`;
            }
          }

          // Check for refund on cancelled bookings
          const rawRefundAmount = Number((row.rawData as any)?.refund_amount) || 0;
          const rawRefundType = (row.rawData as any)?.refund_type;
          const isCancelledWithRefund = info.getValue() === 'cancelled' && rawRefundAmount > 0;

          return (
            <div className="flex flex-col gap-1">
              {/* Main status badge */}
              <span
                className={`inline-flex items-center w-fit px-2 py-1 rounded-lg text-xs font-bold ${getStatusColor(
                  info.getValue() as any
                )}`}
              >
                <span className="capitalize">{info.getValue()}</span>
              </span>

              {/* Refund badge for cancelled bookings with refunds */}
              {isCancelledWithRefund && (
                <span className="inline-flex items-center gap-0.5 w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 whitespace-nowrap">
                  Refund: {CURRENCY_SYMBOL}{rawRefundAmount.toLocaleString()} ({rawRefundType === 'full' ? 'full' : 'partial'})
                </span>
              )}

              {/* Compact recurring info - single line with all info */}
              {isRecurring && (
                <div className="flex items-center gap-1 flex-wrap">
                  {/* Type indicator with session info */}
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      isManualRecurring
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-purple-50 text-purple-700 border border-purple-200'
                    }`}
                    title={isManualRecurring ? 'Manual Recurring Booking' : 'SPOTTS Recurring Booking'}
                  >
                    {isManualRecurring ? '📅' : '🔄'}
                    <span>{sessionInfo}</span>
                    {isManualRecurring && <span className="opacity-70">M</span>}
                  </span>

                  {/* Cancelled sessions indicator - only if there are cancellations */}
                  {cancelledCount > 0 && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                      ✕{cancelledCount}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        },
      }),
      // Hidden column for "newest first" sorting by creation timestamp
      columnHelper.accessor('createdAt', {
        header: 'Created',
        enableHiding: true,
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
      columnVisibility: { createdAt: false },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Tab counts using single source of truth (same logic as filtering)
  const tabCounts = useMemo(() => calculateTabCounts(tableData), [tableData]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Error handling
  if (!venue) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">Please ensure you have a venue associated with your account.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Bookings</h2>
        <p className="text-gray-600 mb-4">{error instanceof Error ? error.message : 'An unexpected error occurred'}</p>
        <button
          onClick={() => invalidateBookingRelated(venue?.id || 0)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Bookings</h1>
          <p className="text-gray-600 text-sm">
            {filteredData.length} {filteredData.length === 1 ? 'booking' : 'bookings'} found
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsManualBookingModalOpen(true)}
            disabled={!can('bookings.create')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600"
            title={can('bookings.create') ? 'Add a manual booking (walk-in, phone booking)' : "You don't have permission to add bookings"}
          >
            <User className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Add Manual Booking</span>
            <span className="sm:hidden">Add</span>
          </button>
          <button
            onClick={() => setIsBlockModalOpen(true)}
            disabled={!can('slots.manage')}
            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-orange-500"
            title={can('slots.manage') ? 'Block a time slot' : "You don't have permission to block slots"}
          >
            <Clock className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Block Time</span>
            <span className="sm:hidden">Block</span>
          </button>
          <button
            onClick={async () => {
              // Check if there's data to export
              if (!filteredData || filteredData.length === 0) {
                await alert({
                  title: 'No Data',
                  message: 'No bookings available to export for the selected filters.',
                  variant: 'info',
                });
                return;
              }

              // Export functionality - generate CSV from filtered data
              // Helper to properly escape CSV values (quote values with commas/quotes/newlines)
              const escapeCSV = (value: string | number | undefined | null): string => {
                if (value === undefined || value === null) return '""';
                const str = String(value);
                // If contains comma, quote, or newline, wrap in quotes and escape internal quotes
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                  return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
              };

              const csvData = filteredData.map((booking) => ({
                Reference: booking.reference,
                Customer: booking.customer,
                Sport: booking.sport,
                Court: booking.court,
                Date: booking.date,
                Time: booking.time,
                Duration: `${booking.duration} mins`,
                Amount: booking.amount.toLocaleString('en-NG'), // Raw number for CSV, no currency symbol
                'Amount (NGN)': booking.amount, // Numeric column for spreadsheet calculations
                Status: booking.status,
                'Payment Status': booking.paymentStatus,
                Type: booking.type === 'recurring' ? 'Recurring' :
                      booking.type === 'external' ? 'External' :
                      booking.type === 'blocked' ? 'Blocked' : 'One-time',
                Source: booking.source || 'app',
              }));

              const headers = Object.keys(csvData[0] || {}).map(escapeCSV).join(',');
              const rows = csvData.map((row) => Object.values(row).map(escapeCSV).join(',')).join('\n');
              const csv = `${headers}\n${rows}`;

              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `bookings-${format(new Date(), 'yyyy-MM-dd')}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors text-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Export</span>
            <span className="sm:hidden">CSV</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white rounded-xl p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-gray-600 text-xs mb-1 truncate">{stat.label}</p>
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stat.value}</p>
              </div>
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${stat.color} flex items-center justify-center flex-shrink-0 ml-2`}
              >
                <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View Mode & Filters - All in one row */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* View Mode Buttons */}
          <div className="flex items-center gap-1 sm:gap-2 mr-2 sm:mr-4 border-r border-gray-200 pr-2 sm:pr-4">
            <button
              onClick={() => setActiveView('list')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap ${
                activeView === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setActiveView('calendar')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap ${
                activeView === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Calendar
            </button>
            <button
              onClick={() => setActiveView('availability')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap ${
                activeView === 'availability'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">Day View</span>
              <span className="sm:hidden">Day</span>
            </button>
          </div>

          {/* Filters - Date range only for list/calendar views */}
          {activeView !== 'availability' && (
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as FilterState['dateRange'] })}
              className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500 cursor-pointer"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Next 12 Months</option>
              <option value="all">All Time</option>
            </select>
          )}

          <select
            value={filters.sport}
            onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500 cursor-pointer"
          >
            {availableSports.map((sport) => (
              <option key={sport} value={sport}>
                {sport === 'all' ? 'All Sports' : sport}
              </option>
            ))}
          </select>

          <select
            value={filters.court}
            onChange={(e) => setFilters({ ...filters, court: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500 cursor-pointer"
          >
            {availableCourts.map((court) => (
              <option key={court.value} value={court.value}>
                {court.label}
              </option>
            ))}
          </select>

          {activeView !== 'availability' && (
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500 cursor-pointer"
            >
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Statuses'
                    : status === 'pending_payment' ? 'Reserved (Awaiting Payment)'
                    : status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          )}

          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value as 'all' | 'manual' | 'spotts' })}
            className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500 cursor-pointer"
          >
            <option value="all">All Sources</option>
            <option value="spotts">Spotts App</option>
            <option value="manual">Manual</option>
          </select>

          {/* Clear Filters Button */}
          {hasActiveFilters(filters) && (
            <button
              onClick={() => setFilters(getDefaultFilters())}
              className="px-3 py-2 border border-gray-300 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 hover:bg-gray-50 flex items-center whitespace-nowrap"
            >
              <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Tabs with Search */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200">
          {/* Tab Buttons */}
          <div className="flex items-center overflow-x-auto px-3 sm:px-4">
            {(['today', 'upcoming', 'recurring', 'cancelled', 'all'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 sm:px-5 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="hidden sm:inline">{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                <span className="sm:hidden">{tab.charAt(0).toUpperCase() + tab.slice(1).substring(0, 3)}</span>
                {' '}
                <span className="text-gray-400">({tabCounts[tab]})</span>
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative px-3 py-2 sm:px-4 sm:py-2">
            <Search className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 absolute left-6 sm:left-7 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 w-full sm:w-48 md:w-64 text-xs sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content: List View or Calendar View */}
      {isPending ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      ) : activeView === 'list' ? (
        /* LIST VIEW */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center space-x-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer"
                  onClick={() => {
                    const booking = row.original;
                    // Handle blocked slots differently
                    if ((booking.type as any) === 'blocked') {
                      setSelectedBlockedSlot(booking.rawData);
                      setIsBlockedSlotModalOpen(true);
                      return;
                    }
                    setSelectedBooking(booking);
                    setIsModalOpen(true);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {table.getRowModel().rows.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No bookings found</h3>
              <p className="text-sm text-gray-500">
                {globalFilter ? 'Try adjusting your search terms.' : 'No bookings match the selected filter.'}
              </p>
            </div>
          )}

          {/* Pagination */}
          <div className="px-3 sm:px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              Showing{' '}
              <span className="font-semibold">
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                -
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}
              </span>{' '}
              of <span className="font-semibold">{table.getFilteredRowModel().rows.length}</span> bookings
            </p>
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-2 sm:px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold text-gray-700">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-2 sm:px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : activeView === 'calendar' ? (
        /* CALENDAR VIEW */
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Calendar Header */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    const newDate = new Date(currentMonth);
                    newDate.setMonth(currentMonth.getMonth() - 1);
                    setCurrentMonth(newDate);
                  }}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <h2 className="text-base sm:text-lg font-bold text-gray-900">
                  {format(currentMonth, 'MMM yyyy')}
                </h2>
                <button
                  onClick={() => {
                    const newDate = new Date(currentMonth);
                    newDate.setMonth(currentMonth.getMonth() + 1);
                    setCurrentMonth(newDate);
                  }}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setCurrentMonth(new Date())}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg font-semibold text-xs sm:text-sm hover:bg-blue-700"
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    // Filter calendar data to only bookings in the current displayed month
                    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
                    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
                    const monthBookings = filteredCalendarData.filter((b) => {
                      const d = new Date(b.date);
                      return d >= monthStart && d <= monthEnd;
                    });
                    if (monthBookings.length === 0) {
                      alert({ title: 'No Data', message: 'No bookings to download for this month.', variant: 'info' });
                      return;
                    }
                    generateCalendarReportCSV(
                      monthBookings,
                      `Month Report - ${format(currentMonth, 'MMMM yyyy')}`
                    );
                  }}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-green-600 text-white rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors"
                  title={`Download ${format(currentMonth, 'MMMM yyyy')} report`}
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2 text-xs sm:text-sm flex-wrap">
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded"></div>
                    <span className="text-gray-600 hidden sm:inline">Spotts</span>
                    <span className="text-gray-600 sm:hidden">App</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded"></div>
                    <span className="text-gray-600 hidden sm:inline">Manual</span>
                    <span className="text-gray-600 sm:hidden">Man</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-purple-500 rounded"></div>
                    <span className="text-gray-600 hidden sm:inline">Recurring</span>
                    <span className="text-gray-600 sm:hidden">Rec</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-orange-500 rounded"></div>
                    <span className="text-gray-600 hidden sm:inline">Blocked</span>
                    <span className="text-gray-600 sm:hidden">Blk</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-teal-500 rounded"></div>
                    <span className="text-gray-600 hidden sm:inline">Third-Party</span>
                    <span className="text-gray-600 sm:hidden">3rd</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded"></div>
                    <span className="text-gray-600 hidden sm:inline">Cancelled</span>
                    <span className="text-gray-600 sm:hidden">Can</span>
                  </div>
                </div>
                <select
                  value={calendarView}
                  onChange={(e) => setCalendarView(e.target.value as 'month' | 'week')}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-semibold text-xs sm:text-sm"
                >
                  <option value="month">Month</option>
                  <option value="week">Week</option>
                </select>
              </div>
            </div>
            {/* Tip for users */}
            <p className="text-xs text-gray-500 mt-2 hidden sm:block">
              💡 Tip: Click on any day to view the daily schedule
            </p>
          </div>

          {/* Calendar Grid */}
          <div className="p-2 sm:p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div
                  key={day}
                  className="text-center text-[10px] sm:text-xs font-bold text-gray-600 py-1 sm:py-2"
                >
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.substring(0, 1)}</span>
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {(() => {
                const days = calendarView === 'week' ? getWeekDays(currentMonth) : getMonthDays(currentMonth);
                // Use filteredCalendarData to respect dropdown filters (sport, court, status)
                const bookingsByDate = groupBookingsByDate(filteredCalendarData);

                return days.map((day, idx) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const rawDayBookings = bookingsByDate[dateStr] || [];

                  // Consolidate blocked slots to avoid calendar clutter
                  const { consolidated: dayBookings, blockedSlotsCount, hasFullDayBlock } =
                    consolidateBlockedSlots(rawDayBookings);

                  const isTodayDate = isToday(day);
                  const isInCurrentMonth = isCurrentMonth(day, currentMonth);

                  // Check if this day is closed (based on operating hours)
                  const isClosed = isClosedDay(day, venue?.operating_hours);

                  // Check if this day is a blackout date
                  const blackoutInfo = isBlackoutDate(day, blackoutDates);

                  // Determine day cell styling
                  const getDayCellClasses = () => {
                    const baseClasses = 'min-h-20 sm:min-h-32 border rounded p-1 sm:p-2 cursor-pointer transition-all hover:shadow-md';

                    if (blackoutInfo.isBlackout) {
                      // Blackout dates: slight red tint
                      return `${baseClasses} border-red-200 bg-red-50`;
                    }

                    if (isClosed) {
                      // Closed days: greyed out
                      return `${baseClasses} border-gray-300 bg-gray-100`;
                    }

                    if (isTodayDate) {
                      return `${baseClasses} border-blue-500 bg-blue-50 border-2`;
                    }

                    if (!isInCurrentMonth && calendarView === 'month') {
                      return `${baseClasses} border-gray-200 bg-gray-50`;
                    }

                    return `${baseClasses} border-gray-200 bg-white hover:bg-gray-50`;
                  };

                  return (
                    <div
                      key={idx}
                      className={getDayCellClasses()}
                      onClick={(e) => {
                        // Only navigate if clicking on the day cell itself, not on a booking
                        if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.day-header')) {
                          setAvailabilityDate(day);
                          setActiveView('availability');
                        }
                      }}
                      title={
                        blackoutInfo.isBlackout
                          ? `Blackout: ${blackoutInfo.reason}`
                          : isClosed
                          ? 'Closed'
                          : `Click to view daily schedule for ${format(day, 'MMM d, yyyy')}`
                      }
                    >
                      {/* Day header - clickable to navigate to availability */}
                      <div className="day-header flex items-center justify-between">
                        <span
                          className={`text-[10px] sm:text-sm font-semibold ${
                            blackoutInfo.isBlackout
                              ? 'text-red-600'
                              : isClosed
                              ? 'text-gray-400'
                              : isTodayDate
                              ? 'text-blue-600'
                              : !isInCurrentMonth && calendarView === 'month'
                              ? 'text-gray-400'
                              : 'text-gray-900'
                          }`}
                        >
                          {day.getDate()}
                        </span>

                        {/* Status indicators */}
                        <div className="flex items-center gap-0.5">
                          {/* Full day block indicator */}
                          {hasFullDayBlock && !blackoutInfo.isBlackout && !isClosed && (
                            <span className="text-[8px] sm:text-[10px] bg-orange-500 text-white px-1 rounded font-bold" title="Full day or extended block">
                              FULL
                            </span>
                          )}
                          {/* Multiple blocked slots badge */}
                          {blockedSlotsCount > 2 && !hasFullDayBlock && (
                            <span className="text-[8px] sm:text-[10px] bg-orange-400 text-white px-1 rounded font-bold" title={`${blockedSlotsCount} blocked slots`}>
                              {blockedSlotsCount}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bookings for this day - sorted by time, scrollable */}
                      <div
                        className="mt-0.5 sm:mt-1 space-y-0.5 sm:space-y-1 max-h-16 sm:max-h-24 overflow-y-auto"
                        onClick={(e) => e.stopPropagation()} // Prevent day cell click when clicking bookings
                      >
                        {dayBookings.map((booking) => (
                          <div
                            key={booking.id}
                            onClick={() => {
                              // Open blocked slot modal for blocked slots
                              if ((booking.type as any) === 'blocked') {
                                setSelectedBlockedSlot(booking.rawData);
                                setIsBlockedSlotModalOpen(true);
                                return;
                              }
                              setSelectedBooking(booking);
                              setIsModalOpen(true);
                            }}
                            className={`${getBookingTypeColor(booking.type, booking.rawData)} border-l-2 rounded px-1 sm:px-2 py-0.5 sm:py-1 text-[8px] sm:text-xs cursor-pointer hover:shadow-md transition-shadow`}
                          >
                            <p className="font-bold truncate">{booking.customer}</p>
                            <p className="truncate hidden sm:block">{booking.court}</p>
                            <p className="truncate hidden sm:block">{booking.time}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      ) : (
        /* DAY VIEW - Horizontal Timeline with Courts */
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Header with Date Navigation and Legend */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Date Navigation */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const newDate = new Date(availabilityDate);
                    newDate.setDate(availabilityDate.getDate() - 1);
                    setAvailabilityDate(newDate);
                  }}
                  className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-base sm:text-lg font-bold text-gray-900">
                  {format(availabilityDate, 'EEE, MMM d, yyyy')}
                </h2>
                <button
                  onClick={() => {
                    const newDate = new Date(availabilityDate);
                    newDate.setDate(availabilityDate.getDate() + 1);
                    setAvailabilityDate(newDate);
                  }}
                  className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setAvailabilityDate(new Date())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700"
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    if (!filteredTimelineBookings || filteredTimelineBookings.length === 0) {
                      alert({ title: 'No Data', message: 'No bookings to download for this day.', variant: 'info' });
                      return;
                    }
                    generateDayReportCSV(
                      filteredTimelineBookings,
                      `Day Report - ${format(availabilityDate, 'dd MMM yyyy')}`
                    );
                  }}
                  className="w-10 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors"
                  title={`Download ${format(availabilityDate, 'dd MMM yyyy')} report`}
                >
                  <Download className="w-5 h-5" />
                </button>

                {/* Booking Count Badge */}
                {scheduleData && (
                  <span className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-bold text-gray-700">
                    {(scheduleData?.one_time_bookings?.length || 0) +
                     (scheduleData?.recurring_instances?.length || 0)}{' '}
                    <span className="font-semibold text-gray-500">
                      {((scheduleData?.one_time_bookings?.length || 0) + (scheduleData?.recurring_instances?.length || 0)) === 1 ? 'Booking' : 'Bookings'}
                    </span>
                    {(scheduleData?.blocked_slots?.length || 0) > 0 && (
                      <span className="text-orange-600 ml-1">
                        + {scheduleData?.blocked_slots?.length} Blocked
                      </span>
                    )}
                  </span>
                )}
              </div>

              {/* Selected Court Indicator */}
              {filters.court !== 'all' && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 font-medium">Viewing:</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                    {availableCourts.find((c) => c.value === filters.court)?.label || filters.court}
                  </span>
                  <button
                    onClick={() => setFilters({ ...filters, court: 'all' })}
                    className="text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Show all
                  </button>
                </div>
              )}

              {/* Legend */}
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-blue-500 rounded border-l-2 border-blue-600"></div>
                  <span className="text-gray-600">Spotts</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-green-500 rounded border-l-2 border-green-600"></div>
                  <span className="text-gray-600">Manual</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-purple-500 rounded border-l-2 border-purple-600"></div>
                  <span className="text-gray-600">Recurring</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-orange-500 rounded border-l-2 border-orange-600"></div>
                  <span className="text-gray-600">Blocked</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-red-500 rounded border-l-2 border-red-600"></div>
                  <span className="text-gray-600">Cancelled</span>
                </div>
              </div>
            </div>
          </div>

          {/* Day Summary - Shows booking stats for the selected date */}
          <DaySummary
            selectedDate={availabilityDate}
            bookings={filteredTimelineBookings}
            isLoading={isScheduleLoading}
          />

          {/* Timeline */}
          <DayViewTimeline
            selectedDate={availabilityDate}
            courts={timelineCourts}
            operatingHours={venue?.operating_hours}
            isLoading={isScheduleLoading || isCourtsLoading}
            bookings={filteredTimelineBookings}
            onBookingClick={(booking: any) => {
              // Handle blocked slots differently
              if (booking.type === 'blocked') {
                setSelectedBlockedSlot(booking.rawData);
                setIsBlockedSlotModalOpen(true);
                return;
              }

              // Transform schedule API data to match OneTimeBooking/RecurringBooking structure
              // Schedule API returns flat fields (court_name, sport_name, date)
              // but BookingDetailsModal expects nested (court.name, court.sport.name, booking_date)
              const raw = booking.rawData || {};
              const compatibleRawData = {
                ...raw,
                booking_reference: raw.booking_reference || booking.booking_reference,
                booking_date: raw.date || raw.booking_date || format(availabilityDate, 'yyyy-MM-dd'),
                start_time: raw.start_time || booking.start_time,
                end_time: raw.end_time || booking.end_time,
                status: raw.status || booking.status,
                payment_status: raw.payment_status || 'pending',
                source: raw.source || booking.type,
                customer_name: raw.customer_name || booking.customer_name,
                customer_phone: raw.customer_phone,
                // Build nested court/sport structure from flat fields
                court: raw.court || { name: raw.court_name || 'Unknown Court', sport: { name: raw.sport_name || 'Sport' } },
                // Build user from flat fields if not present
                user: raw.user || (raw.customer_name ? { name: raw.customer_name, phone: raw.customer_phone } : null),
                // Provide sensible defaults for missing pricing fields
                final_price: raw.amount || raw.final_price || 0,
                venue_share: raw.venue_share || 0,
                original_price: raw.original_price || raw.amount || 0,
                discount_amount: raw.discount_amount || 0,
                platform_commission: raw.platform_commission || 0,
              };

              const bookingRow: BookingRow = {
                id: booking.id,
                reference: compatibleRawData.booking_reference || `${booking.type.toUpperCase()}-${booking.id}`,
                sport: booking.sport_name || raw.sport_name || 'N/A',
                customer: booking.customer_name || 'N/A',
                court: raw.court_name || raw.court?.name || 'N/A',
                date: compatibleRawData.booking_date,
                time: `${booking.start_time} - ${booking.end_time}`,
                duration: 60,
                amount: compatibleRawData.final_price,
                status: booking.status as any,
                paymentStatus: compatibleRawData.payment_status,
                type: booking.type === 'recurring' ? 'recurring' : 'onetime',
                createdAt: raw.created_at || '',
                rawData: compatibleRawData,
              };
              setSelectedBooking(bookingRow);
              setIsModalOpen(true);
            }}
            onEmptySlotClick={(courtId, time) => {
              // Could open manual booking modal pre-filled with court and time
              setIsManualBookingModalOpen(true);
            }}
          />
        </div>
      )}

      {/* Booking Details Modal */}
      {isModalOpen && selectedBooking && (
        selectedBooking.type === 'recurring' ? (
          <RecurringBookingDetailsModal
            booking={selectedBooking.rawData as RecurringBooking}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedBooking(null);
            }}
            // Single-session cancel SUSPENDED Jul 2026 (owner decision, D21-UI):
            // the backend endpoint never existed in a working state — the button
            // always errored. Re-enable by restoring this prop once a real
            // endpoint is built on RecurringBookingService.
            // onCancelInstance={openCancelInstanceModal}
            onRescheduleInstance={openRescheduleInstanceModal}
          />
        ) : selectedBooking.type === 'manual_recurring' ? (
          <ManualRecurringBookingDetailsModal
            group={selectedBooking.rawData as ManualRecurringGroup}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedBooking(null);
            }}
            onCancelSession={openCancelBookingModal}
            onRescheduleSession={openRescheduleBookingModal}
            onCancelAllSessions={openCancelAllSessionsModal}
            onDeleteGroup={handleDeleteRecurringGroup}
            onEditSession={openEditSessionModal}
            onBulkUpdatePrice={openBulkPriceModal}
          />
        ) : (selectedBooking.type as string) === 'blocked' || (selectedBooking.type as string) === 'external' ? (
          <BlockedSlotDetailsModal
            blockedSlot={selectedBooking.rawData}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedBooking(null);
            }}
            onUnblock={(selectedBooking.type as string) === 'blocked' ? handleUnblockSlot : undefined}
          />
        ) : (
          <BookingDetailsModal
            booking={selectedBooking.rawData as OneTimeBooking}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedBooking(null);
            }}
            onCancel={openCancelBookingModal}
            onReschedule={openRescheduleBookingModal}
            onDelete={handleDeleteManualBooking}
            onCheckIn={handleCheckInBooking}
            allowCheckIn={can('checkin')}
            allowReschedule={can('bookings.edit')}
            allowCancel={can('bookings.cancel')}
            allowDelete={can('bookings.cancel')}
          />
        )
      )}

      {/* Cancellation Reason Modal */}
      <CancellationReasonModal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setSelectedInstanceId(null);
          setRefundEstimate(null);
          setSelectedManualBookingId(null);
        }}
        onConfirm={selectedInstanceId ? handleConfirmCancelInstance : handleConfirmCancelBooking}
        title={selectedInstanceId ? 'Cancel Session' : 'Cancel Booking'}
        bookingType={
          selectedInstanceId
            ? 'session'  // Cancelling a single session from a recurring booking
            : selectedBooking?.type === 'recurring'
              ? 'entire_booking'  // Cancelling entire recurring booking
              : 'session'  // Cancelling a one-time booking (treat as single session)
        }
        bookingInfo={{
          reference: selectedBooking?.reference,
          customer_name: selectedBooking?.customer,
          customer_email: selectedBooking?.type === 'manual_recurring'
            ? (selectedBooking.rawData as ManualRecurringGroup)?.customer_email
            : (selectedBooking?.rawData as OneTimeBooking)?.customer_email,
          // For manual_recurring type, set source as 'manual'
          source: selectedBooking?.type === 'manual_recurring'
            ? 'manual'
            : (selectedBooking?.rawData as OneTimeBooking)?.source,
          // For manual recurring: get the specific session's price, not the group total
          final_price: selectedBooking?.type === 'manual_recurring' && selectedManualBookingId
            ? (selectedBooking.rawData as ManualRecurringGroup)?.bookings?.find(
                (b) => b.id === selectedManualBookingId
              )?.final_price
            : (selectedBooking?.rawData as OneTimeBooking)?.final_price,
        }}
        refundInfo={refundEstimate ? {
          refund_amount: refundEstimate.refund_amount,
          refund_type: refundEstimate.refund_policy,
          refund_policy: refundEstimate.refund_policy,
          refund_timeline: 'Immediate',
          gateway_fee: refundEstimate.gateway_fee,
          venue_gateway_fee_cost: parseFloat(refundEstimate.venue_gateway_fee_cost),
          is_split_payment: refundEstimate.is_split_payment,
          split_refund_given: refundEstimate.split_refund_given,
          vendor_deduction: refundEstimate.vendor_deduction,
          venue_share: refundEstimate.venue_share,
          platform_commission: refundEstimate.platform_commission,
          has_commission: refundEstimate.has_commission,
          original_amount: refundEstimate.original_amount,
        } : undefined}
      />

      {/* Cancel All Sessions Modal */}
      <CancellationReasonModal
        isOpen={isCancelAllModalOpen}
        onClose={() => {
          setIsCancelAllModalOpen(false);
          setSelectedRecurringGroupId(null);
        }}
        onConfirm={handleConfirmCancelAllSessions}
        title="Cancel All Upcoming Sessions"
        bookingType="entire_booking"
        bookingInfo={{
          reference: selectedRecurringGroupId || '',
          customer_name: selectedBooking?.customer,
          customer_email: (selectedBooking?.rawData as ManualRecurringGroup)?.customer_email,
          source: 'manual',
          final_price: (selectedBooking?.rawData as ManualRecurringGroup)?.total_amount,
        }}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={isRescheduleModalOpen}
        onClose={() => {
          setIsRescheduleModalOpen(false);
          setSelectedInstanceId(null);
          setSelectedManualBookingId(null);
        }}
        booking={
          // For one-time bookings, pass the booking directly
          selectedBooking?.type === 'onetime'
            ? selectedBooking.rawData as OneTimeBooking
            // For manual recurring groups, find the specific booking being rescheduled
            : selectedBooking?.type === 'manual_recurring' && selectedManualBookingId
              ? (selectedBooking.rawData as ManualRecurringGroup).bookings.find(b => b.id === selectedManualBookingId)
              : undefined
        }
        recurringInstance={selectedInstanceId && selectedBooking?.type === 'recurring'
          ? (selectedBooking.rawData as RecurringBooking).instances?.find(i => i.id === selectedInstanceId)
          : undefined}
        onReschedule={handleConfirmReschedule}
        operatingHours={venue?.operating_hours}
      />

      {/* Block Time Slot Modal */}
      <BlockTimeSlotModal
        isOpen={isBlockModalOpen}
        onClose={() => setIsBlockModalOpen(false)}
        onSubmit={handleBlockSlot}
        courts={Array.isArray(courts) ? courts : []}
        operatingHours={venue?.operating_hours}
      />

      {/* Add Manual Booking Modal */}
      <AddManualBookingModal
        isOpen={isManualBookingModalOpen}
        onClose={() => setIsManualBookingModalOpen(false)}
        onSubmit={handleCreateManualBooking}
        courts={Array.isArray(courts) ? courts : []}
        venueId={venue.id}
        operatingHours={venue?.operating_hours}
      />

      {/* Edit Manual Session Modal */}
      <EditManualSessionModal
        session={editingSession}
        isOpen={isEditSessionModalOpen}
        onClose={() => {
          setIsEditSessionModalOpen(false);
          setEditingSession(null);
        }}
        onConfirm={handleConfirmEditSession}
      />

      {/* Bulk Group Price Modal */}
      <BulkGroupPriceModal
        groupId={bulkPriceGroupId}
        upcomingSessionCount={bulkPriceUpcomingCount}
        totalSessionCount={bulkPriceTotalCount}
        isOpen={isBulkPriceModalOpen}
        onClose={() => {
          setIsBulkPriceModalOpen(false);
          setBulkPriceGroupId(null);
          setBulkPriceUpcomingCount(0);
          setBulkPriceTotalCount(0);
        }}
        onConfirm={handleConfirmBulkPrice}
      />

      {/* Blocked Slot Details Modal */}
      {selectedBlockedSlot && (
        <BlockedSlotDetailsModal
          blockedSlot={selectedBlockedSlot}
          isOpen={isBlockedSlotModalOpen}
          onClose={() => {
            setIsBlockedSlotModalOpen(false);
            setSelectedBlockedSlot(null);
          }}
          onUnblock={handleUnblockSlot}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default BookingsManagementNew;
